import { FC, useMemo, useCallback, useState } from 'react'
import Button, { ButtonTheme } from 'components/features/pfm/Button/Button'
import styles from 'components/atoms/inputs/GridSelect/GridSelect.styl'
import cn from 'classnames'
import { Controller, useFormContext } from 'react-hook-form'

type ControllerRules = NonNullable<Parameters<typeof Controller>[0]['rules']>

export type GridSelectOption = {
  title: string
  icon?: string
  theme?: ButtonTheme
  selectedTheme?: ButtonTheme
  slug: string
}

type GridSelectProps = {
  options: GridSelectOption[]
  initialOptions?: string[]
  max?: number
  theme?: ButtonTheme
  selectedTheme?: ButtonTheme
  onChange: (options: string[]) => void
}
export const GridSelect: FC<GridSelectProps> = (props) => {
  const { options, initialOptions = [], theme = 'event', selectedTheme = 'goal', onChange, max } = props

  const [selected, setSelected] = useState(initialOptions)
  const selectedSet = useMemo(() => new Set(selected), [selected])

  const handleClick = useCallback((slug: string) => {
    const isSet = selectedSet.has(slug)
    if (!isSet && max && selected.length >= max) {
      return
    }

    if (isSet) {
      selectedSet.delete(slug)
    } else {
      selectedSet.add(slug)
    }

    const nextSelected = Array.from(selectedSet)

    onChange(nextSelected)
    setSelected(nextSelected)
  }, [selected, onChange, max, selectedSet])

  const isMaxSelected = !!(max && selectedSet.size >= max)

  const buttonsChild = useMemo(() => {
    return options.map((option) => {
      const { slug, title, icon, theme: optionTheme, selectedTheme: selectedOptionTheme } = option

      const isSelected = selectedSet.has(slug)
      const isDisabled = isMaxSelected && !isSelected

      const currentTheme = isSelected
        ? selectedOptionTheme || selectedTheme
        : optionTheme || theme

      return (
        <div key={ slug } className={ cn(styles.button, isDisabled && styles.disabled) }>
          <Button
            size='square'
            iconSlug={ icon }
            text={ title }
            theme={ currentTheme }
            onClick={ () => handleClick(slug) }
            shadow='light'
            disabled={ isDisabled }
          />
        </div>
      )
    })
  }, [options, theme, handleClick, selectedTheme, isMaxSelected, selectedSet])

  return (
    <div className={ styles.gridSelect }>
      { buttonsChild }
    </div>
  )
}

export type GridSelectControllerProps =
  & Omit<GridSelectProps, 'onChange'>
  & {
    rules?: ControllerRules
    name: string
  }

export const GridSelectController: FC<GridSelectControllerProps> = (props) => {
  const { name, rules, ...restProps } = props
  const { control } = useFormContext()

  const inputRenderer = useCallback(({ field: { onChange }}) => (
    <GridSelect
      { ...restProps }
      onChange={ onChange }
    />
  ), [restProps])

  return (
    <Controller
      control={ control }
      name={ name }
      render={ inputRenderer }
      rules={ rules }
    />
  )
}

